// Service Worker registration for PWA functionality
// This file contains the service worker registration logic

import { logger } from './logger';
import { buildApiUrl, config } from '../config';

const isProduction = config.isProduction;
const swUrl = '/sw.js';

// Show notification for service worker events
const showNotification = (message: string, isError = false) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(`Service Worker ${isError ? 'Error' : 'Info'}`, {
      body: message,
      icon: '/favicon.ico',
      tag: 'service-worker',
    });
  }

  // Also log in development
  if (isError) {
    logger.error(message, { component: 'ServiceWorker' });
  } else {
    logger.info(message, { component: 'ServiceWorker' });
  }
};

export const registerSW = (): void => {
  if ('serviceWorker' in navigator && isProduction) {
    navigator.serviceWorker
      .register(swUrl)
      .then((_registration) => {
        showNotification(`Service Worker registered successfully`);
      })
      .catch((registrationError) => {
        showNotification(`Service Worker registration failed: ${registrationError.message}`, true);
      });
  }
};

export const unregisterSW = (): void => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        showNotification(`Service Worker unregistration failed: ${error.message}`, true);
      });
  }
};

/**
 * Prevede VAPID base64 klic na Uint8Array pro PushManager.subscribe()
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Ziska VAPID verejny klic z API
 */
async function fetchVapidKey(): Promise<string | null> {
  try {
    const res = await fetch(buildApiUrl('push/vapid-key'), {
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.vapidPublicKey || null;
  } catch {
    return null;
  }
}

/**
 * Prihlasi se k push notifikacim
 * Vrati true pokud uspesne, false pokud selhalo
 */
export const subscribeToPush = async (): Promise<boolean> => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    logger.warn('Push notifikace nejsou v tomto prohlizeci podporovany', {
      component: 'ServiceWorker',
    });
    return false;
  }

  try {
    // Pozadame o opravneni
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      logger.info('Uzivatel zamitl push notifikace', { component: 'ServiceWorker' });
      return false;
    }

    // Ziskame VAPID klic
    const vapidKey = await fetchVapidKey();
    if (!vapidKey) {
      logger.warn('VAPID klic neni dostupny — push notifikace nenakonfigurovane', {
        component: 'ServiceWorker',
      });
      return false;
    }

    const registration = await navigator.serviceWorker.ready;

    // Zkontrolujeme existujici subscription
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey).buffer as ArrayBuffer,
      });
    }

    // Odesleme subscription na backend
    const res = await fetch(buildApiUrl('push/subscribe'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))),
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!))),
        },
      }),
    });

    if (!res.ok) {
      logger.error('Chyba pri registraci push subscription na backend', {
        component: 'ServiceWorker',
      });
      return false;
    }

    logger.info('Push notifikace aktivovany', { component: 'ServiceWorker' });
    return true;
  } catch (err: any) {
    logger.error(`Push subscription selhala: ${err.message}`, { component: 'ServiceWorker' });
    return false;
  }
};

/**
 * Odhlas se z push notifikaci
 */
export const unsubscribeFromPush = async (): Promise<boolean> => {
  if (!('serviceWorker' in navigator)) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return true;

    // Zrus na serveru
    await fetch(buildApiUrl('push/unsubscribe'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    });

    // Zrus v prohlizeci
    await subscription.unsubscribe();

    logger.info('Push notifikace deaktivovany', { component: 'ServiceWorker' });
    return true;
  } catch (err: any) {
    logger.error(`Push unsubscribe selhala: ${err.message}`, { component: 'ServiceWorker' });
    return false;
  }
};
